import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { handleApiError } from "../components/apiErrorHandler";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";

const STATUS_SEVERITY = { pending: "warning", processed: "success", rejected: "danger" };

const RefillRequests = () => {

    const [requests,           setRequests]           = useState([]);
    const [totalRecords,       setTotalRecords]       = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [loading,            setLoading]            = useState(true);
    const [itemsDialogVisible, setItemsDialogVisible] = useState(false);
    const [selectedItems,      setSelectedItems]      = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => { fetchRequests(); }, [location.search, queryParams]);

    const fetchRequests = async () => {
        setLoading(true);
        const params   = new URLSearchParams(location.search);
        const refillId = params.get("id");
        try {
            if (refillId) {
                const res = await axiosInstance.get(`/api/refill-requests/${refillId}/`);
                setRequests([res.data]);
                setTotalRecords(1);
            } else {
                const res = await axiosInstance.get("/api/refill-requests/", { params: queryParams });
                setRequests(res.data.results);
                setTotalRecords(res.data.count);
            }
        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axiosInstance.patch(`/api/refill-requests/${id}/`, { status: newStatus });
            fetchRequests();
        } catch (err) {
            handleApiError(err);
        }
    };

    // ── Process: navigate to Prescriptions with refill context ───────────────
    // Quantity is NOT set here — pharmacist sets it in the prescription dialog
    // where they can see dosage and frequency to calculate correctly.
    const handleProcessRefill = (row) => {
        navigate("/prescriptions", {
            state: {
                prescriptionId:   row.prescription,
                refillRequestId:  row.id,
                refillRequestDays: row.duration,   // shown as banner in dialog
                refillItems: (row.items || []).map(i => ({
                    product_id:         i.product_id,
                    product_name:       i.product_name,
                    requested_quantity: null,       // pharmacist sets in dialog
                })),
            }
        });
    };

    // ── Column templates ──────────────────────────────────────────────────────
    const itemsTemplate = (row) => {
        const items = row.items || [];
        if (!items.length) return <span style={{ color: "#999" }}>—</span>;
        const first = items[0];
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13 }}>{first.product_name}</span>
                {items.length > 1 && (
                    <Button
                        icon="pi pi-list"
                        className="p-button-text p-button-sm"
                        tooltip={`View all ${items.length} items`}
                        style={{ padding: "2px 6px" }}
                        onClick={() => { setSelectedItems(items); setItemsDialogVisible(true); }}
                    />
                )}
            </div>
        );
    };

    const daysTemplate = (row) => (
        row.duration
            ? <span style={{ fontWeight: 600, color: "#1d4ed8" }}>{row.duration} days</span>
            : <span style={{ color: "#bbb" }}>—</span>
    );

    const notesTemplate = (row) => (
        row.notes
            ? <span style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>"{row.notes}"</span>
            : <span style={{ color: "#ccc" }}>—</span>
    );

    const statusTemplate = (row) => (
        <Tag
            value={row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
            severity={STATUS_SEVERITY[row.status] || "info"}
        />
    );

    const actionTemplate = (row) => {
        const isPending = row.status === "pending";
        return (
            <div style={{ display: "flex", gap: 6 }}>
                <Button
                    label="Process"
                    icon="pi pi-play"
                    className="p-button-success p-button-sm"
                    disabled={!isPending}
                    onClick={() => handleProcessRefill(row)}
                />
                <Button
                    label="Reject"
                    icon="pi pi-times"
                    className="p-button-danger p-button-sm"
                    disabled={!isPending}
                    onClick={() => updateStatus(row.id, "rejected")}
                />
            </div>
        );
    };

    const linkedTemplate = (row) => {
        const invoices    = row.linked_invoices    || [];
        const prescription = row.linked_prescription;
        if (!invoices.length && !prescription) {
            return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
        }
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {prescription && (
                    <button
                        onClick={() => navigate("/prescriptions", { state: { prescriptionId: prescription.id } })}
                        style={{
                            background: "#eff6ff", border: "1px solid #bfdbfe",
                            borderRadius: 6, padding: "2px 8px", fontSize: 11.5,
                            color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit",
                            display: "flex", alignItems: "center", gap: 4,
                        }}
                    >
                        <i className="pi pi-file" style={{ fontSize: 10 }} />
                        Rx #{prescription.id?.toString().slice(-6)}
                        <i className="pi pi-arrow-right" style={{ fontSize: 9 }} />
                    </button>
                )}
                {invoices.map(inv => (
                    <button
                        key={inv.id}
                        onClick={() => navigate(`/invoices?invoiceId=${inv.id}`)}
                        style={{
                            background: "#f0fdf4", border: "1px solid #bbf7d0",
                            borderRadius: 6, padding: "2px 8px", fontSize: 11.5,
                            color: "#15803d", cursor: "pointer", fontFamily: "inherit",
                            display: "flex", alignItems: "center", gap: 4,
                        }}
                    >
                        <i className="pi pi-receipt" style={{ fontSize: 10 }} />
                        {inv.invoice_number}
                        <i className="pi pi-arrow-right" style={{ fontSize: 9 }} />
                    </button>
                ))}
            </div>
        );
    };

    const columns = [
        { accessor: "customer_name", Header: "Customer" },
        { accessor: "created_at",    Header: "Date",            dataType: "date" },
        { accessor: "items",         Header: "Items",           bodyTemplate: itemsTemplate },
        { accessor: "duration",      Header: "Days Requested",  bodyTemplate: daysTemplate },
        { accessor: "notes",         Header: "Patient Notes",   bodyTemplate: notesTemplate },
        { accessor: "status",        Header: "Status",          bodyTemplate: statusTemplate,   filtered: true },
        { accessor: "linked",        Header: "Linked Records",  bodyTemplate: linkedTemplate },
        { accessor: "id",            Header: "Actions",         bodyTemplate: actionTemplate },
    ];

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    data={requests}
                    columns={columns}
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                    tabletitle="Customer Refill Requests"
                    loading={loading}
                    showEditColumn={false}
                />

                {/* Items detail dialog */}
                <Dialog
                    header="Requested Items"
                    visible={itemsDialogVisible}
                    style={{ width: 400 }}
                    onHide={() => setItemsDialogVisible(false)}
                >
                    {selectedItems.map((item, idx) => (
                        <div key={idx} style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "8px 0",
                            borderBottom: idx < selectedItems.length - 1 ? "1px solid #eee" : "none",
                            fontSize: 13,
                        }}>
                            <span>{item.product_name}</span>
                            <span style={{ color: "#666" }}>
                                {item.requested_quantity != null ? `Qty: ${item.requested_quantity}` : "—"}
                            </span>
                        </div>
                    ))}
                </Dialog>
            </div>
        </div>
    );
};

export default RefillRequests;